import { useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'app_theme';

function readStored(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function applyToBody(theme: Theme): void {
  if (typeof document === 'undefined') {
    return;
  }
  const body = document.body;
  if (theme === 'light') {
    body.classList.add('theme-light');
    body.classList.remove('theme-dark');
  } else {
    body.classList.remove('theme-light');
    body.classList.add('theme-dark');
  }
}

// Apply once at module load so that the body has the right class
// before React mounts (avoids a flash of dark mode).
if (typeof document !== 'undefined') {
  applyToBody(readStored());
}

const THEME_EVENT = 'app-theme-change';

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => readStored());

  // Synchronise toutes les instances du hook quand une instance change le theme
  useEffect(() => {
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<Theme>).detail;
      if (detail === 'dark' || detail === 'light') {
        setThemeState((prev) => (prev === detail ? prev : detail));
      }
    };
    window.addEventListener(THEME_EVENT, handler);
    return () => window.removeEventListener(THEME_EVENT, handler);
  }, []);

  useEffect(() => {
    applyToBody(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
    // Broadcast aux autres instances du hook (navbar, home, etc.)
    window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: theme }));
  }, [theme]);

  const setTheme = (t: Theme): void => setThemeState(t);
  const toggle = (): void => setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));

  return { theme, setTheme, toggle };
}
